import { useState, useEffect } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { Save, User, Briefcase, Bell, Shield, Plus, Trash2, Loader2, Search, Edit2, X, CreditCard, Building, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import StripeCheckoutModal from '../components/StripeCheckoutModal';
import { Capacitor } from '@capacitor/core';

export default function Settings() {
  const [profileName, setProfileName] = useState('');
  const [incomes, setIncomes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [plaidAccounts, setPlaidAccounts] = useState([]);
  const [primaryAccountId, setPrimaryAccountId] = useState(null);
  const [isAccountsCollapsed, setIsAccountsCollapsed] = useState(false);
  const [linkToken, setLinkToken] = useState(null);

  const [newIncomeName, setNewIncomeName] = useState('');
  const [newIncomeNickname, setNewIncomeNickname] = useState('');
  const [newIncomeAmount, setNewIncomeAmount] = useState('');
  const [newIncomeFrequency, setNewIncomeFrequency] = useState('Bi-Weekly');
  const [isCreatingIncome, setIsCreatingIncome] = useState(false);
  const [incomeError, setIncomeError] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const [plaidDeposits, setPlaidDeposits] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  const [editingIncomeId, setEditingIncomeId] = useState(null);
  const [editIncomeName, setEditIncomeName] = useState('');
  const [editIncomeNickname, setEditIncomeNickname] = useState('');
  const [editIncomeAmount, setEditIncomeAmount] = useState('');
  const [editIncomeFrequency, setEditIncomeFrequency] = useState('Bi-Weekly');

  const [subscriptionStatus, setSubscriptionStatus] = useState('trialing');
  const [trialEndsAt, setTrialEndsAt] = useState(null);

  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const [checkoutClientSecret, setCheckoutClientSecret] = useState(null);
  const [loadingCheckout, setLoadingCheckout] = useState(false);

  const [editingBufferId, setEditingBufferId] = useState(null);
  const [bufferValue, setBufferValue] = useState('');
  const [biometricsEnabled, setBiometricsEnabled] = useState(false);

  useEffect(() => {
    const savedUser = localStorage.getItem('thread_user');
    if (savedUser) {
      const u = JSON.parse(savedUser);
      setProfileName(u.display_name || '');
      setSubscriptionStatus(u.subscription_status);
      setTrialEndsAt(u.trial_ends_at);
    }

    if (Capacitor.isNativePlatform()) {
      setBiometricsEnabled(localStorage.getItem('thread_biometrics_enabled') === 'true');
    }
    
    // Check if returning from a successful Stripe Checkout
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('session_id')) {
      // Refresh user data from backend because the webhook might have updated it
      api.get('/auth/me').then(res => {
        localStorage.setItem('thread_user', JSON.stringify(res.data.user));
        setSubscriptionStatus(res.data.user.subscription_status);
        window.dispatchEvent(new Event('thread_auth_updated'));
        
        // Clean up the URL
        window.history.replaceState({}, document.title, '/settings');
        toast.success('Thank you for upgrading to Thread Pro!');
      }).catch(console.error);
    }

    fetchIncomes();
    fetchBankStatus();
    fetchLinkToken();
  }, []);

  const handleToggleBiometrics = async () => {
    const newValue = !biometricsEnabled;
    
    if (newValue) {
      try {
        const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
        const result = await NativeBiometric.isAvailable();
        if (result.isAvailable) {
          await NativeBiometric.verifyIdentity({
            reason: "Confirm biometric setup",
            title: "Verify Identity",
          });
          localStorage.setItem('thread_biometrics_enabled', 'true');
          setBiometricsEnabled(true);
          toast.success('Biometric lock enabled successfully!');
        } else {
          toast.error('Biometric authentication is not available on this device.');
        }
      } catch (e) {
        console.error(e);
        toast.error('Biometric verification failed.');
      }
    } else {
      localStorage.setItem('thread_biometrics_enabled', 'false');
      setBiometricsEnabled(false);
      toast.success('Biometric lock disabled.');
    }
  };

  const fetchBankStatus = async () => {
    try {
      const res = await api.get('/plaid/status');
      if (res.data.linked) {
        setPlaidAccounts(res.data.accounts || []);
        setPrimaryAccountId(res.data.primary_account_id);
        if (res.data.primary_account_id) {
          setIsAccountsCollapsed(true);
        }
      } else {
        setPlaidAccounts([]);
        setPrimaryAccountId(null);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchLinkToken = async () => {
    try {
      const res = await api.post('/plaid/create-link-token');
      setLinkToken(res.data.link_token);
    } catch (err) {
      console.error(err);
    }
  };

  const onPlaidSuccess = async (public_token, metadata) => {
    setLoading(true);
    try {
      await api.post('/plaid/exchange-public-token', { public_token });
      toast.success('Bank account connected successfully!');
      toast('Reminder: If you linked a different bank, check your Income Sources and Buckets as transaction names may differ.', { icon: '⚠️', duration: 8000 });
      await fetchBankStatus();
    } catch (err) {
      console.error('Exchange failed', err);
      toast.error('Failed to link bank account.');
    } finally {
      setLoading(false);
    }
  };

  const { open: openPlaidLink, ready: plaidReady } = usePlaidLink({
    token: linkToken,
    onSuccess: onPlaidSuccess,
  });

  const fetchIncomes = async () => {
    try {
      const res = await api.get('/incomes');
      setIncomes(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = () => {
    const savedUser = JSON.parse(localStorage.getItem('thread_user') || '{}');
    savedUser.display_name = profileName;
    localStorage.setItem('thread_user', JSON.stringify(savedUser));
    window.dispatchEvent(new Event('thread_settings_updated'));
    toast.success('Profile saved!');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmationText !== 'DELETE') {
      toast.error('You must type DELETE in all caps to confirm.');
      return;
    }
    
    setIsDeleting(true);
    try {
      await api.delete('/auth/me');
      localStorage.removeItem('thread_user');
      localStorage.removeItem('thread_name');
      window.dispatchEvent(new Event('thread_auth_error'));
      window.location.href = '/';
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete account. Please try again.');
      setIsDeleting(false);
    }
  };

  const handleDisconnectBank = async () => {
    if (confirm("Are you sure you want to disconnect your bank account? Autopilot will pause until a new account is connected.")) {
      setLoading(true);
      try {
        await api.post('/plaid/disconnect');
        setPlaidAccounts([]);
        setPrimaryAccountId(null);
        toast.success("Bank disconnected.");
      } catch (err) {
        console.error(err);
        toast.error("Failed to disconnect bank.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSetPrimaryAccount = async (account_id) => {
    setLoading(true);
    try {
      await api.post('/plaid/set-primary-account', { account_id });
      setPrimaryAccountId(account_id);
      toast.success("Primary account updated!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update primary account.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBuffer = async (account_id) => {
    try {
      await api.put(`/plaid/accounts/${account_id}/buffer`, { min_balance_buffer: bufferValue });
      setPlaidAccounts(plaidAccounts.map(a => a.account_id === account_id ? { ...a, min_balance_buffer: bufferValue } : a));
      setEditingBufferId(null);
      toast.success("Safety net buffer updated!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update safety net buffer.");
    }
  };



  const handleSubscribe = async () => {
    setLoadingCheckout(true);
    try {
      const res = await api.post('/stripe/create-checkout-session');
      setCheckoutClientSecret(res.data.clientSecret);
      setLoadingCheckout(false);
    } catch (err) {
      console.error(err);
      toast.error('Failed to start checkout. Please try again.');
      setLoadingCheckout(false);
    }
  };

  const handleAddIncome = async (e) => {
    e.preventDefault();
    setIncomeError('');
    try {
      const res = await api.post('/incomes', {
        name: newIncomeName,
        nickname: newIncomeNickname || newIncomeName,
        amount: parseFloat(newIncomeAmount) || 0,
        frequency: newIncomeFrequency
      });
      setIncomes([...incomes, res.data]);
      setNewIncomeName('');
      setNewIncomeAmount('');
      setNewIncomeFrequency('Bi-Weekly');
      setIsCreatingIncome(false);
      window.dispatchEvent(new Event('thread_settings_updated'));
    } catch (err) {
      console.error(err);
      setIncomeError(err.response?.data?.error || 'Failed to save income source');
    }
  };

  const handleDeleteIncome = async (id) => {
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      return;
    }
    
    try {
      await api.delete(`/incomes/${id}`);
      setIncomes(incomes.filter(i => i.id !== id));
      setConfirmDeleteId(null);
      window.dispatchEvent(new Event('thread_settings_updated'));
    } catch (err) {
      console.error(err);
    }
  };

  const handleScanDeposits = async () => {
    setIsCreatingIncome(false);
    setScanning(true);
    setShowScanner(true);
    try {
      const res = await api.get('/plaid/deposits');
      setPlaidDeposits(res.data.deposits);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 400) {
        toast.error("Please link your bank account on the Dashboard first!");
        setShowScanner(false);
      } else {
        toast.error("Error scanning deposits.");
      }
    } finally {
      setScanning(false);
    }
  };

  const handleTrackScannedIncome = async (deposit) => {
    try {
      const res = await api.post('/incomes', {
        name: deposit.name,
        nickname: deposit.name,
        amount: deposit.amount,
        frequency: 'Bi-Weekly' // Default, user can edit later
      });
      setIncomes([...incomes, res.data]);
      window.dispatchEvent(new Event('thread_settings_updated'));
      
      setPlaidDeposits(plaidDeposits.filter(d => d.id !== deposit.id));
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to track income source');
    }
  };

  const handleStartEdit = (inc) => {
    setEditingIncomeId(inc.id);
    setEditIncomeName(inc.name);
    setEditIncomeNickname(inc.nickname || inc.name);
    setEditIncomeAmount(inc.amount);
    setEditIncomeFrequency(inc.frequency || 'Bi-Weekly');
  };

  const handleUpdateIncome = async (e, id) => {
    e.preventDefault();
    try {
      const res = await api.put(`/incomes/${id}`, {
        name: editIncomeName,
        nickname: editIncomeNickname,
        amount: parseFloat(editIncomeAmount) || 0,
        frequency: editIncomeFrequency
      });
      setIncomes(incomes.map(i => i.id === id ? res.data : i));
      setEditingIncomeId(null);
      window.dispatchEvent(new Event('thread_settings_updated'));
    } catch (err) {
      console.error(err);
      toast.error('Failed to update income');
    }
  };

  return (
    <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-lg)', maxWidth: '900px' }}>
      <div>
        <h2 style={{ margin: 0 }}>Settings</h2>
        <span className="text-muted">Manage your profile and application preferences.</span>
      </div>

      <div className="mobile-flex-col" style={{ display: 'flex', gap: 'var(--spacing-lg)' }}>
        
        {/* Settings Navigation */}
        <div className="settings-nav mobile-full-width" style={{ width: '200px', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <button className="btn btn-glass" onClick={() => document.getElementById('section-income').scrollIntoView({behavior: 'smooth'})} style={{ justifyContent: 'flex-start', border: 'none' }}><Briefcase size={16} /> Income Sources</button>
          <button className="btn btn-glass" onClick={() => document.getElementById('section-bank').scrollIntoView({behavior: 'smooth'})} style={{ justifyContent: 'flex-start', border: 'none' }}><Building size={16} /> Bank Connection</button>
          <button className="btn btn-glass" onClick={() => document.getElementById('section-profile').scrollIntoView({behavior: 'smooth'})} style={{ justifyContent: 'flex-start', border: 'none' }}><User size={16} /> Profile</button>
          <button className="btn btn-glass" onClick={() => document.getElementById('section-billing').scrollIntoView({behavior: 'smooth'})} style={{ justifyContent: 'flex-start', border: 'none' }}><CreditCard size={16} /> Billing</button>
          <button className="btn btn-glass" onClick={() => document.getElementById('section-security').scrollIntoView({behavior: 'smooth'})} style={{ justifyContent: 'flex-start', border: 'none' }}><Shield size={16} /> Security</button>
        </div>

        {/* Settings Form */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--spacing-lg)' }}>
          
          <div id="section-income" className="glass" style={{ padding: 'var(--spacing-lg)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Briefcase size={20} color="var(--primary)" /> Income Sources
              </h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn btn-glass" onClick={handleScanDeposits} disabled={scanning} style={{ padding: '0.4rem 0.8rem', color: 'var(--success)', borderColor: 'rgba(34, 197, 94, 0.3)' }}>
                  {scanning ? <Loader2 size={16} style={{ animation: 'spin 2s linear infinite' }} /> : <Search size={16} />} 
                  {scanning ? 'Scanning...' : 'Scan Bank'}
                </button>
                <button className="btn btn-primary" onClick={() => { setIsCreatingIncome(!isCreatingIncome); setShowScanner(false); }} style={{ padding: '0.4rem 0.8rem' }}>
                  <Plus size={16} /> Add Custom
                </button>
              </div>
            </div>

            {showScanner && (
              <div style={{ background: 'rgba(34, 197, 94, 0.05)', border: '1px solid rgba(34, 197, 94, 0.2)', padding: '1rem', borderRadius: 'var(--radius-sm)', marginBottom: '1.5rem' }}>
                <h4 style={{ margin: '0 0 1rem 0', color: 'var(--success)' }}>Discovered Deposits</h4>
                {scanning ? (
                  <div style={{ textAlign: 'center', padding: '1rem' }}><Loader2 className="text-muted" style={{ animation: 'spin 2s linear infinite' }} /></div>
                ) : plaidDeposits.length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No deposits found in the last 60 days.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {plaidDeposits.map(d => (
                      <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,0,0,0.2)', padding: '0.8rem', borderRadius: 'var(--radius-sm)' }}>
                        <div>
                          <strong style={{ display: 'block', fontSize: '0.9rem' }}>{d.name}</strong>
                          <span className="text-muted" style={{ fontSize: '0.8rem' }}>{new Date(d.date).toLocaleDateString()}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <strong style={{ color: 'var(--success)' }}>+${d.amount.toFixed(2)}</strong>
                          <button className="btn btn-primary" onClick={() => handleTrackScannedIncome(d)} style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>
                            Track this Income
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
              Define your different paychecks here. You can set up specific allocation rules for each income source in the Allocations tab.
            </p>

            {isCreatingIncome && (
              <form onSubmit={handleAddIncome} style={{ background: 'rgba(255,255,255,0.02)', padding: '1rem', borderRadius: 'var(--radius-sm)', marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div className="mobile-flex-col" style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div className="mobile-full-width" style={{ flex: 1, minWidth: '200px' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Exact Bank Name</label>
                    <input type="text" value={newIncomeName} onChange={e => setNewIncomeName(e.target.value)} placeholder="e.g., DIRECT DEP GUSTO" required />
                  </div>
                  <div className="mobile-full-width" style={{ flex: 1, minWidth: '150px' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Display Name (Nickname)</label>
                    <input type="text" value={newIncomeNickname} onChange={e => setNewIncomeNickname(e.target.value)} placeholder="e.g., Matt's Salary" />
                  </div>
                  <div className="mobile-full-width" style={{ width: '150px' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Amount ($)</label>
                    <input type="number" step="any" value={newIncomeAmount} onChange={e => setNewIncomeAmount(e.target.value)} required />
                  </div>
                  <div className="mobile-full-width" style={{ width: '150px' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Frequency</label>
                    <select value={newIncomeFrequency} onChange={e => setNewIncomeFrequency(e.target.value)}>
                      <option value="Weekly">Weekly</option>
                      <option value="Bi-Weekly">Bi-Weekly</option>
                      <option value="Semi-Monthly">Semi-Monthly</option>
                      <option value="Monthly">Monthly</option>
                    </select>
                  </div>
                  <button type="submit" className="btn btn-primary" style={{ padding: '0.8rem 1rem' }}>Save</button>
                </div>
                {incomeError && <div style={{ color: 'var(--danger)', fontSize: '0.85rem' }}>{incomeError}</div>}
              </form>
            )}

            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}><Loader2 className="text-muted" style={{ animation: 'spin 2s linear infinite' }} /></div>
            ) : incomes.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', background: 'rgba(0,0,0,0.2)', borderRadius: 'var(--radius-sm)' }}>
                No income sources added yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {incomes.map(inc => (
                  <div key={inc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.03)', padding: '1rem', borderRadius: 'var(--radius-sm)' }}>
                    {editingIncomeId === inc.id ? (
                      <form onSubmit={(e) => handleUpdateIncome(e, inc.id)} style={{ width: '100%', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div style={{ flex: 1, minWidth: '150px' }}>
                          <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Display Name</label>
                          <input type="text" value={editIncomeNickname} onChange={e => setEditIncomeNickname(e.target.value)} required style={{ padding: '0.4rem', fontSize: '0.9rem' }} />
                        </div>
                        <div style={{ width: '100px' }}>
                          <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Amount</label>
                          <input type="number" step="any" value={editIncomeAmount} onChange={e => setEditIncomeAmount(e.target.value)} required style={{ padding: '0.4rem', fontSize: '0.9rem' }} />
                        </div>
                        <div style={{ width: '120px' }}>
                          <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)' }}>Freq</label>
                          <select value={editIncomeFrequency} onChange={e => setEditIncomeFrequency(e.target.value)} style={{ padding: '0.4rem', fontSize: '0.9rem' }}>
                            <option value="Weekly">Weekly</option>
                            <option value="Bi-Weekly">Bi-Weekly</option>
                            <option value="Semi-Monthly">Semi-Monthly</option>
                            <option value="Monthly">Monthly</option>
                          </select>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button type="submit" className="btn btn-primary" style={{ padding: '0.4rem 0.8rem' }}><Save size={16} /></button>
                          <button type="button" className="btn btn-glass" onClick={() => setEditingIncomeId(null)} style={{ padding: '0.4rem 0.8rem' }}><X size={16} /></button>
                        </div>
                      </form>
                    ) : (
                      <>
                        <div>
                          <strong style={{ display: 'block', fontSize: '1.1rem' }}>{inc.nickname || inc.name}</strong>
                          {inc.nickname && inc.nickname !== inc.name && (
                            <span style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Bank Match: {inc.name}</span>
                          )}
                          <span className="text-muted" style={{ fontSize: '0.85rem' }}>{inc.frequency}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <strong>${inc.amount.toLocaleString(undefined, {minimumFractionDigits: 2})}</strong>
                          <button className="btn btn-glass" style={{ padding: '0.4rem', color: 'var(--text-main)' }} onClick={() => handleStartEdit(inc)}>
                            <Edit2 size={16} />
                          </button>
                          {confirmDeleteId === inc.id ? (
                            <button className="btn btn-primary" style={{ padding: '0.4rem 0.8rem', backgroundColor: 'var(--danger)', color: '#fff', border: 'none' }} onClick={() => handleDeleteIncome(inc.id)}>
                              Confirm
                            </button>
                          ) : (
                            <button className="btn btn-glass" style={{ padding: '0.4rem', color: 'var(--danger)', borderColor: 'rgba(239, 68, 68, 0.3)' }} onClick={() => handleDeleteIncome(inc.id)}>
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div id="section-bank" className="glass" style={{ padding: 'var(--spacing-lg)' }}>
            <h3 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Building size={20} color="var(--primary)" /> Bank Connection
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1.5rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--surface-border)' }}>
                {plaidAccounts.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                      <h4 
                        style={{ margin: '0', fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setIsAccountsCollapsed(!isAccountsCollapsed)}
                      >
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--success)' }}></div>
                        Connected Accounts
                        {isAccountsCollapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                      </h4>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button className="btn btn-primary" onClick={() => openPlaidLink()} disabled={loading || !plaidReady} style={{ padding: '0.4rem 0.8rem', fontSize: '0.9rem' }}>
                          Add/Edit Accounts
                        </button>
                        <button className="btn btn-glass" style={{ color: 'var(--danger)', border: 'none', padding: '0.4rem 0.8rem', fontSize: '0.9rem' }} onClick={handleDisconnectBank} disabled={loading}>
                          Disconnect All
                        </button>
                      </div>
                    </div>
                    
                    {!primaryAccountId && (
                       <div style={{ padding: '0.75rem', background: 'rgba(234, 179, 8, 0.1)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(234, 179, 8, 0.3)', color: 'var(--warning)', fontSize: '0.85rem' }}>
                         <strong>⚠️ Action Required:</strong> Please select a Primary Autopilot account from the list below. Autopilot will exclusively scan this account for paychecks and bills.
                       </div>
                    )}
                    
                    {!isAccountsCollapsed && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}>
                        {plaidAccounts.map(acc => (
                          <div key={acc.account_id} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: 'var(--radius-sm)', border: primaryAccountId === acc.account_id ? '1px solid var(--primary)' : '1px solid transparent' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <p style={{ margin: '0 0 0.25rem 0', fontWeight: 'bold' }}>{acc.bankName} <span style={{ opacity: 0.5, fontSize: '0.85rem' }}>({acc.type})</span></p>
                                <span className="text-muted" style={{ fontSize: '0.9rem' }}>Account ending in •••• {acc.mask}</span>
                              </div>
                              <div>
                                {primaryAccountId === acc.account_id ? (
                                  <span style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                    <CheckCircle2 size={16} /> Primary Autopilot
                                  </span>
                                ) : (
                                  <button className="btn btn-glass" onClick={() => handleSetPrimaryAccount(acc.account_id)} disabled={loading} style={{ fontSize: '0.85rem' }}>
                                    Set as Primary
                                  </button>
                                )}
                              </div>
                            </div>
                            
                            {/* Safety Net Settings */}
                            <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                              <div>
                                <strong style={{ display: 'block', fontSize: '0.9rem', color: 'var(--text-main)' }}><Shield size={14} style={{ verticalAlign: 'middle', marginRight: '4px', color: 'var(--warning)' }} /> Overdraft Protection (Safety Net)</strong>
                                <span className="text-muted" style={{ fontSize: '0.8rem', display: 'block', maxWidth: '300px', marginTop: '0.25rem' }}>We will skip allocating your paychecks if your balance is below this amount.</span>
                              </div>
                              <div>
                                {editingBufferId === acc.account_id ? (
                                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <span style={{ color: 'var(--text-muted)' }}>$</span>
                                    <input 
                                      type="number" 
                                      step="any"
                                      value={bufferValue} 
                                      onChange={(e) => setBufferValue(e.target.value)} 
                                      style={{ width: '80px', padding: '0.3rem', fontSize: '0.9rem' }}
                                    />
                                    <button className="btn btn-primary" onClick={() => handleUpdateBuffer(acc.account_id)} style={{ padding: '0.3rem 0.6rem' }}><Save size={14} /></button>
                                    <button className="btn btn-glass" onClick={() => setEditingBufferId(null)} style={{ padding: '0.3rem 0.6rem' }}><X size={14} /></button>
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <strong style={{ fontSize: '1rem' }}>${parseFloat(acc.min_balance_buffer || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</strong>
                                    <button className="btn btn-glass" onClick={() => { setEditingBufferId(acc.account_id); setBufferValue(acc.min_balance_buffer || '0'); }} style={{ padding: '0.3rem' }}><Edit2 size={14} /></button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                      <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.2rem', color: 'var(--danger)' }}>Not Connected</h4>
                      <p className="text-muted" style={{ margin: 0, fontSize: '0.9rem', maxWidth: '300px' }}>
                        You must connect a primary account for Autopilot to detect your paychecks and bills.
                      </p>
                    </div>
                    <button className="btn btn-primary" onClick={() => openPlaidLink()} disabled={loading || !plaidReady}>
                      Connect Bank
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div id="section-profile" className="glass" style={{ padding: 'var(--spacing-lg)' }}>
            <h3 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <User size={20} color="var(--primary)" /> Profile Details
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-muted)' }}>Display Name</label>
                <div className="mobile-flex-col" style={{ display: 'flex', gap: '1rem' }}>
                  <input 
                    type="text" 
                    value={profileName} 
                    onChange={(e) => setProfileName(e.target.value)} 
                    className="mobile-full-width"
                    style={{ width: '100%', maxWidth: '300px' }}
                  />
                  <button className="btn btn-primary" onClick={handleSaveProfile}><Save size={18} /> Update</button>
                </div>
              </div>
            </div>
          </div>

          <div id="section-billing" className="glass" style={{ padding: 'var(--spacing-lg)' }}>
            <h3 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <CreditCard size={20} color="var(--primary)" /> Billing & Subscription
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1.5rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--surface-border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                  <div>
                    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.2rem' }}>Thread Pro</h4>
                    {subscriptionStatus === 'active' ? (
                      <span style={{ color: 'var(--success)', fontWeight: 'bold' }}>Active</span>
                    ) : subscriptionStatus === 'trialing' ? (
                      <span style={{ color: 'var(--primary)', fontWeight: 'bold' }}>Trialing (Expires {trialEndsAt ? new Date(trialEndsAt).toLocaleDateString() : 'Unknown'})</span>
                    ) : (
                      <span style={{ color: 'var(--danger)', fontWeight: 'bold' }}>Expired</span>
                    )}
                    <p className="text-muted" style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', maxWidth: '400px' }}>
                      Thread Pro enables the 24/7 background webhook engine that automatically routes your paychecks and auto-pays your bills.
                    </p>
                  </div>
                  
                  {subscriptionStatus !== 'active' && (
                    <div style={{ textAlign: 'right' }}>
                      <h2 style={{ margin: '0 0 0.5rem 0' }}>$8<span style={{ fontSize: '1rem', color: 'var(--text-muted)' }}>/mo</span></h2>
                      <button className="btn btn-primary" onClick={handleSubscribe} disabled={loadingCheckout}>
                        {loadingCheckout && <Loader2 size={14} style={{ animation: 'spin 2s linear infinite', marginRight: '0.5rem' }} />} Upgrade to Pro
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {Capacitor.isNativePlatform() && (
            <div className="glass" style={{ padding: 'var(--spacing-lg)', marginBottom: 'var(--spacing-lg)' }}>
              <h3 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Shield size={20} className="text-primary" /> Security & Access
              </h3>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h4 style={{ margin: 0, color: 'var(--text-main)' }}>Biometric Lock</h4>
                  <p className="text-muted" style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>
                    Require Fingerprint or Face ID to unlock the app on launch and resume.
                  </p>
                </div>
                <div>
                  <div 
                    onClick={handleToggleBiometrics}
                    style={{
                      width: '50px',
                      height: '26px',
                      backgroundColor: biometricsEnabled ? 'var(--primary)' : 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '13px',
                      position: 'relative',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <div 
                      style={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: '#ffffff',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: '3px',
                        left: biometricsEnabled ? '27px' : '3px',
                        transition: 'left 0.2s',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div id="section-security" className="glass" style={{ padding: 'var(--spacing-lg)' }}>
            <h3 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--danger)' }}>
              <Shield size={20} /> Danger Zone
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div style={{ background: 'rgba(239, 68, 68, 0.05)', padding: '1.5rem', borderRadius: 'var(--radius-md)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--danger)' }}>Delete Account & Wipe Data</h4>
                <p className="text-muted" style={{ margin: '0 0 1rem 0', fontSize: '0.9rem' }}>
                  This will permanently delete your account, wipe all allocation logic, revoke Plaid bank access, and immediately cancel any active Stripe subscriptions. This action cannot be undone.
                </p>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <input 
                    type="text" 
                    placeholder="Type DELETE to confirm" 
                    value={deleteConfirmationText}
                    onChange={(e) => setDeleteConfirmationText(e.target.value)}
                    style={{ padding: '0.6rem', border: '1px solid var(--danger)', borderRadius: 'var(--radius-sm)', background: 'rgba(0,0,0,0.2)', color: 'white' }}
                  />
                  <button 
                    className="btn btn-primary" 
                    style={{ background: 'var(--danger)', borderColor: 'var(--danger)' }} 
                    onClick={() => setIsDeleting(true)}
                    disabled={isDeleting}
                  >
                    Permanently Delete Account
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
        {isDeleting && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div className="glass" style={{ padding: '2rem', maxWidth: '400px', width: '90%', borderRadius: 'var(--radius-lg)' }}>
              <h3 style={{ marginTop: 0, color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Shield size={20} /> Delete Account</h3>
              <p>This action is irreversible. All of your data, banking connections, and history will be permanently destroyed.</p>
              <p>Type <strong>DELETE</strong> to confirm.</p>
              <input type="text" className="input" value={deleteConfirmationText} onChange={e => setDeleteConfirmationText(e.target.value)} placeholder="DELETE" style={{ width: '100%', marginBottom: '1rem' }} />
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button className="btn btn-glass" onClick={() => { setIsDeleting(false); setDeleteConfirmationText(''); }} style={{ flex: 1 }}>Cancel</button>
                <button className="btn btn-primary" style={{ flex: 1, background: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={handleDeleteAccount} disabled={deleteConfirmationText !== 'DELETE'}>Permanently Delete</button>
              </div>
            </div>
          </div>
        )}

        <StripeCheckoutModal 
          clientSecret={checkoutClientSecret} 
          onClose={() => setCheckoutClientSecret(null)} 
        />
      </div>
    </div>
  );
}
